import { applyPatches, produce, produceWithPatches } from 'immer';
import type { Page } from '@/components/canvas/document/publicTypes';
import type { SliceDeps } from '@/components/canvas/store/deps';
import type { HistorySnapshot } from '@/components/canvas/store/types';
import { cloneHistorySnapshot } from './snapshot';

export const HISTORY_MAX = 50;

export function createHistoryEngine({ set, get }: Pick<SliceDeps, 'set' | 'get'>) {
  const commitHistorySnapshot = (
    recipe: (draft: HistorySnapshot) => void,
    base: HistorySnapshot,
  ) => {
    const { _history, _historyIndex } = get();
    const [next, forwardPatches, inversePatches] = produceWithPatches(base, recipe);

    if (forwardPatches.length === 0) {
      set({ _pendingBase: null });
      return;
    }

    const nextHistory = [
      ..._history.slice(0, _historyIndex + 1),
      { forwardPatches, inversePatches },
    ].slice(-HISTORY_MAX);

    set({
      pages: next.pages,
      currentPageId: next.currentPageId,
      workspaceElements: next.workspaceElements,
      _history: nextHistory,
      _historyIndex: nextHistory.length - 1,
      canUndo: true,
      canRedo: false,
      _pendingBase: null,
    });
  };

  /**
   * 基于 pushHistory 时的 _pendingBase 提交历史（一次手势一条 undo）。
   *
   * ⚠️ workspaceElements 始终取当前 store 的值，而不是 _pendingBase 中的版本。
   * 原因：任何绕过 mutate 的静默操作（如 updateElementSilentInDocument）都可能使
   * _pendingBase.workspaceElements 过期；若直接用作 immer 基础状态并写回 store，
   * 会把后续写入的工作区元素全部抹掉。
   * pages 仍使用 _pendingBase 快照，保证跨多步操作的 undo 语义正确。
   */
  const mutate = (recipe: (draft: HistorySnapshot) => void) => {
    const { pages, currentPageId, workspaceElements, _pendingBase } = get();
    const base: HistorySnapshot = {
      pages: _pendingBase?.pages ?? pages,
      currentPageId: _pendingBase?.currentPageId ?? currentPageId,
      workspaceElements, // 始终使用最新值，防止陈旧快照覆盖工作区元素
    };
    commitHistorySnapshot(recipe, base);
  };

  const mutateSilent = (recipe: (draft: Page) => void) => {
    const { pages, currentPageId } = get();
    const nextPages = produce(pages, (draft) => {
      const c = draft.find((cv) => cv.id === currentPageId);
      if (c) recipe(c);
    });
    if (nextPages === pages) return;
    set({ pages: nextPages });
  };

  return { mutate, mutateSilent };
}

export function createHistoryActions({ set, get }: SliceDeps) {
  return {
    pushHistory: () => {
      const { pages, currentPageId, workspaceElements } = get();
      set({
        _pendingBase: cloneHistorySnapshot({ pages, currentPageId, workspaceElements }),
      });
    },

    /** 取消未提交的手势（如拖拽未移动就松手），避免下次 mutate 套用陈旧快照 */
    cancelPendingHistory: () => {
      set({ _pendingBase: null });
    },

    undo: () => {
      const { _history, _historyIndex, pages, currentPageId, workspaceElements } = get();
      if (_historyIndex < 0) return;
      const entry = _history[_historyIndex];
      if (!entry) return;
      const current: HistorySnapshot = { pages, currentPageId, workspaceElements };
      const restored = applyPatches(current, entry.inversePatches);
      set({
        pages: restored.pages,
        currentPageId: restored.currentPageId,
        workspaceElements: restored.workspaceElements,
        _historyIndex: _historyIndex - 1,
        canUndo: _historyIndex - 1 >= 0,
        canRedo: true,
        selectedElementIds: [],
        _pendingBase: null,
      });
    },

    redo: () => {
      const { _history, _historyIndex, pages, currentPageId, workspaceElements } = get();
      if (_historyIndex >= _history.length - 1) return;
      const entry = _history[_historyIndex + 1];
      if (!entry) return;
      const current: HistorySnapshot = { pages, currentPageId, workspaceElements };
      const restored = applyPatches(current, entry.forwardPatches);
      set({
        pages: restored.pages,
        currentPageId: restored.currentPageId,
        workspaceElements: restored.workspaceElements,
        _historyIndex: _historyIndex + 1,
        canUndo: true,
        canRedo: _historyIndex + 1 < _history.length - 1,
        selectedElementIds: [],
        _pendingBase: null,
      });
    },
  };
}
